import { io, Socket } from "socket.io-client";
import { accessTokenStore, fn } from "../../../stores";
import { get } from "svelte/store";
import { type TSocketEventData } from "../../types";
import { Pages } from "../../enums";
import { ethers } from "@credenza3/core-web-evm-ext";

const socketClients = new Map<string, Socket>();

const disconnectSocket = async (wssUrl: string) => {
  const socket = socketClients.get(wssUrl);
  socket?.disconnect();
  socketClients.delete(wssUrl);
};

const connectToSocket = async (wssUrl: string): Promise<Socket> => {
  if (socketClients.has(wssUrl)) return socketClients.get(wssUrl) as Socket;

  const socket = io(wssUrl, {
    auth: {
      bearer_token: get(accessTokenStore),
    },
  });

  await new Promise((resolve, reject) => {
    socket.once("connect_error", (err) => reject(err));
    socket.once("connect", () => resolve(socket));
    setTimeout(
      () => reject(new Error("Cannot connect to websocket server")),
      5000
    );
  });

  socketClients.set(wssUrl, socket);
  return socket;
};

const connectSocketForSignatures = async (wssUrl: string) => {
  const socket = await connectToSocket(wssUrl);
  socket.on("passport_id/sign", onSignRequest);
};

export const forwardTransaction = async (
  wssUrl: string,
  unsignedSerializedTx: string
): Promise<ethers.TransactionResponse> => {
  const socket = await connectToSocket(wssUrl);
  return new Promise((resolve, reject) => {
    socket.emit(
      "contracts/forward_transaction",
      {
        payload: {
          unsigned_serialized_meta_tx: unsignedSerializedTx,
        },
      },
      (data: { payload: any; error: { message: string } }) => {
        disconnectSocket(wssUrl);
        if (data.error) {
          console.error(data.error);
          return reject(new Error("Transaction failed"));
        }

        resolve(data.payload);
      }
    );
  });
};

const signDataAndEmitEvent = async (
  wssUrl: string,
  signature: string,
  headers: TSocketEventData["headers"]
) => {
  try {
    if (!socketClients.has(wssUrl))
      throw new Error("Socket client not connected");
    socketClients.get(wssUrl)?.emit(headers.emit_event, {
      headers: headers.emit_event_headers,
      payload: { signature },
    });
  } catch (err) {
    console.error(err);
  }
};
const onSignRequest = async (data: TSocketEventData) => {
  const { openUI } = get(fn);
  openUI(Pages.SIGN, {
    message: data.payload.message,
    socketEventDataHeaders: data.headers,
  });
};
export default {
  connectSocketForSignatures,
  signDataAndEmitEvent,
};
